import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	leavePolicy: null,
	error: "",
	loading: false,
};

// ADD_leavePolicy
export const addSingleLeavePolicy = createAsyncThunk(
	"leavePolicy/addSingleLeavePolicy",
	async (values) => {
		try {
			const { data } = await axios({
				method: "post",
				url: `leave-policy/`,
				data: {
					...values,
				},
			});
			toast.success("LeavePolicy Added");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			const errorMessage =
				error.response && error.response.data && error.response.data.message
					? error.response.data.message
					: "Error in adding LeavePolicy. Try again.";
			toast.error(errorMessage);
			console.error(errorMessage);
			return {
				message: "error",
			};
		}
	}
);


export const createValidityEntry = createAsyncThunk(
	"leavePolicy/createValidityEntry ",
	async (values) => {
		try {
			const { data } = await axios({
				method: "post",
				url: `leave-policy/validityEntry`,
				data: {
					...values,
				},
			});
			toast.success("LeavePolicy Added");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in adding LeavePolicy try again");
			console.log(error.message);
			return {
				message: "error",
			};
		}
	}
);

export const addSingleLeaveType = createAsyncThunk(
  "leavePolicy/addSingleLeaveType",
  async (values) => {
    try {
      const { data } = await axios({
        method: "post",
        url: `leave-policy/createLeaveType`,
        data: {
          ...values,
        },
      });
      toast.success("LeaveType Added");
      return {
        data,
        message: "success",
      };
    } catch (error) {
      // Show API error message in toast
      const errorMessage = error.response?.data?.message || "Error in adding LeaveType try again";
      toast.error(errorMessage);
      console.log(error.message);
      return {
        message: "error",
      };
    }
  }
);

export const leaveAllocationAllUsers = createAsyncThunk(
	"leavePolicy/leaveAllocationAllUsers",
	async (values) => {
		try {
			const { data } = await axios({
				method: "post",
				url: `leave-policy/leaveAllocationAllUsers`,
				data: {
					...values,
				},
			});

			// Check if the response indicates that no new allocations were created
			if (data.message === 'No new leave allocations to create') {
				toast.warning("No new leave allocations created, as duplicates were found.");
			} else {
				toast.success("Leave Allocations Added Successfully");
			}

			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in adding Leave Allocations. Try again.");
			console.log(error.message);
			return {
				message: "error",
			};
		}
	}
);

// DELETE_leavePolicy
export const deleteLeavePolicy = createAsyncThunk(
	"leavePolicy/deleteLeavePolicy ",
	async (id) => {
		try {
			const resp = await axios({
				method: "delete",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json;charset=UTF-8",
				},
				url: `leave-policy/${id}`,
			});

			toast.success("LeavePolicy Deleted");
			return {
				data: resp.data.id,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in deleting leavePolicy try again");
			console.log(error.message);
		}
	}
);

// leavePolicy_DETAILS
export const loadSingleLeavePolicy = createAsyncThunk(
	"leavePolicy/loadSingleLeavePolicy",
	async (id) => {
		try {
			const data = await axios.get(`leave-policy/${id}`);
			return data;
		} catch (error) {
			console.log(error.message);
		}
	}
);

export const fetchValidityEntry = createAsyncThunk(
	"leavePolicy/fetchValidityEntry",
	async () => {
		try {
			const { data } = await axios.get(`leave-policy/getValidity/all`);
			return data;
		} catch (error) {
			console.log(error.message);
		}
	}
);

export const getLeavePoliciesWithLimit = createAsyncThunk(
	"leavePolicy/getLeavePoliciesWithLimit",
	async () => {
		try {
			const { data } = await axios.get(`leave-policy/all/getLeavePoliciesWithLimit`);
			return data;
		} catch (error) {
			console.log(error.message);
		}
	}
);

export const getDefaultValidity = createAsyncThunk(
	"leavePolicy/getDefaultValidity",
	async () => {
		try {
			const { data } = await axios.get(`leave-policy/get/defaultValidity`);
			return data;
		} catch (error) {
			console.log(error.message);
		}
	}
);

export const getBulkUploadSampleData = createAsyncThunk(
	"leavePolicy/getBulkUploadSampleData",
	async (validityId) => {
		try {
			const { data } = await axios.get(`leave-policy/leaveAllocation/getBulkUploadSampleData?validity_id=${validityId}`);
			return data;
		} catch (error) {
			console.log(error.message);
			throw error;
		}
	}
);

export const bulkUpdateLeaveAllocation = createAsyncThunk(
	"leavePolicy/bulkUpdateLeaveAllocation",
	async ({ allocations, forceUpdate = false, validityId }) => {
		try {
			// Make request with arraybuffer to handle both JSON and Excel
			const response = await axios.post(`leave-policy/bulkUpdateLeaveAllocation`, {
				allocations,
				forceUpdate,
				validityId
			}, {
				responseType: 'arraybuffer',
			});
			
			// Check if response is Excel file based on Content-Type
			const contentType = response.headers['content-type'] || '';
			if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
				// Handle Excel download
				const blob = new Blob([response.data], { type: contentType });
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				
				// Extract filename from Content-Disposition header
				const contentDisposition = response.headers['content-disposition'];
				let filename = 'Leave_Allocation_Bulk_Update.xlsx';
				if (contentDisposition) {
					const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
					if (filenameMatch) {
						filename = filenameMatch[1];
					}
				}
				
				link.setAttribute('download', filename);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
				
				// Show success message
				toast.success('Bulk update completed! Downloading results file...', { autoClose: 5000 });
				
				// Return a summary (we can't parse the Excel, so return a generic success)
				return {
					message: 'Bulk update completed',
					results: {
						summary: {
							totalProcessed: allocations.length,
							totalCreated: 0,
							totalUpdated: 0,
							totalUnchanged: 0,
							totalErrors: 0,
						}
					}
				};
			} else {
				// Handle JSON response - convert arraybuffer to text then parse
				let data;
				try {
					const text = new TextDecoder('utf-8').decode(response.data);
					data = JSON.parse(text);
				} catch (parseError) {
					console.error('Error parsing JSON response:', parseError);
					throw new Error('Failed to parse server response');
				}
				
				const { summary } = data.results;
				
				// Build success message
				let successMessage = 'Bulk update completed: ';
				const parts = [];
				
				if (summary.totalCreated > 0) {
					parts.push(`${summary.totalCreated} created`);
				}
				if (summary.totalUpdated > 0) {
					parts.push(`${summary.totalUpdated} updated`);
				}
				if (summary.totalUnchanged > 0) {
					parts.push(`${summary.totalUnchanged} unchanged`);
				}
				
				successMessage += parts.join(', ');
				
				// Show detailed success message
				if (summary.totalCreated > 0 || summary.totalUpdated > 0 || summary.totalUnchanged > 0) {
					toast.success(successMessage, { autoClose: 5000 });
				}
				
				// Show errors if any
				if (summary.totalErrors > 0) {
					toast.warning(`${summary.totalErrors} errors occurred during bulk update`);
				}
				
				return data;
			}
		} catch (error) {
			const errorMessage = error.response?.data?.message || "Error in bulk update. Try again.";
			toast.error(errorMessage);
			console.log(error.message);
			throw error;
		}
	}
);

// leavePolicyS
export const loadAllLeavePolicy = createAsyncThunk(
	"leavePolicy/loadAllLeavePolicy",
	async () => {
		try {
			const { data } = await axios.get(`leave-policy?query=all`);
			return data;
		} catch (error) {
			console.log(error.message);
		}
	}
);

export const getAllLeaveAllocation = createAsyncThunk(
	"leavePolicy/getAllLeaveAllocation",
	async () => {
		try {
			const { data } = await axios.get(`leave-policy/getAllLeaveAllocation`);
			return data;
		} catch (error) {
			console.log(error.message);
		}
	}
);

export const loadAllLeaveType = createAsyncThunk(
	"leavePolicy/loadAllLeaveType",
	async () => {
		try {
			const { data } = await axios.get(`leave-policy/leaveType`);
			return data;
		} catch (error) {
			console.log(error.message);
		}
	}
);

export const updateLeavePolicy = createAsyncThunk(
	"leavePolicy/updateLeavePolicy",
	async ({ id, values }) => {
		try {
			const { data } = await axios({
				method: "put",

				url: `leave-policy/${id}`,
				data: {
					...values,
				},
			});
			toast.success("LeavePolicy Updated");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in updating LeavePolicy try again");
			console.log(error.message);
			return {
				message: "error",
			};
		}
	}
);

export const updateDefaultValidityId = createAsyncThunk(
	"leavePolicy/updateDefaultValidityId",
	async ({ company_id, values }) => {
		try {
			const { data } = await axios({
				method: "put",

				url: `leave-policy/updateDefaultValidityId/${company_id}`,
				data: {
					...values,
				},
			});
			toast.success("Default LeavePolicy Updated");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in updating Default LeavePolicy try again");
			console.log(error.message);
			return {
				message: "error",
			};
		}
	}
);

export const updateValidityApplyFor = createAsyncThunk(
	"leavePolicy/updateValidityApplyFor",
	async (values) => {
		try {
			const { data } = await axios({
				method: "put",
				url: `leave-policy/updateValidityApplyFor`,
				data: {
					...values,
				},
			});
			// Don't show toast here - component will handle it
			return {
				data,
				message: "success",
			};
		} catch (error) {
			console.log(error.message);
			return {
				message: "error",
			};
		}
	}
);


export const updateValidityEntry = createAsyncThunk(
	"leavePolicy/updateValidityEntry",
	async ({ id, values }) => {
		try {
			const { data } = await axios({
				method: "put",

				url: `leave-policy/updateValidityEntry/${id}`,
				data: {
					...values,
				},
			});
			toast.success("LeavePolicy Updated");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in updating LeavePolicy try again");
			console.log(error.message);
			return {
				message: "error",
			};
		}
	}
);
const leavePolicySlice = createSlice({
	name: "leavePolicy",
	initialState,
	reducers: {
		clearLeavePolicy: (state) => {
			state.leavePolicy = null;
		},
	},
	extraReducers: (builder) => {
		// 1) ====== builders for loadAllLeavePolicy ======

		builder.addCase(loadAllLeavePolicy.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(loadAllLeavePolicy.fulfilled, (state, action) => {
			state.loading = false;
			state.list = action.payload;
		});

		builder.addCase(loadAllLeavePolicy.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 1) ====== builders for getAllLeaveAllocation ======

		builder.addCase(getAllLeaveAllocation.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(getAllLeaveAllocation.fulfilled, (state, action) => {
			state.loading = false;
			state.allocatedLeave = action.payload;
		});

		builder.addCase(getAllLeaveAllocation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});


		// 1) ====== builders for loadAllLeaveType ======

		builder.addCase(loadAllLeaveType.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(loadAllLeaveType.fulfilled, (state, action) => {
			state.loading = false;
			state.list = action.payload;
		});

		builder.addCase(loadAllLeaveType.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 2) ====== builders for addSingleLeavePolicy  ======

		builder.addCase(addSingleLeavePolicy.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(addSingleLeavePolicy.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(addSingleLeavePolicy.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 2) ====== builders for createValidityEntry  ======

		builder.addCase(createValidityEntry.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(createValidityEntry.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(createValidityEntry.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 2) ====== builders for addSingleLeaveType  ======


		builder.addCase(addSingleLeaveType.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(addSingleLeaveType.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(addSingleLeaveType.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 2) ====== builders for leaveAllocationAllUsers  ======


		builder.addCase(leaveAllocationAllUsers.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(leaveAllocationAllUsers.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(leaveAllocationAllUsers.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});


		// 3) ====== builders for loadSingleLeavePolicy ======

		builder.addCase(loadSingleLeavePolicy.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(loadSingleLeavePolicy.fulfilled, (state, action) => {
			state.loading = false;
			state.leavePolicy = action.payload.data;
		});

		builder.addCase(loadSingleLeavePolicy.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 3) ====== builders for fetchValidityEntry ======

		builder.addCase(fetchValidityEntry.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(fetchValidityEntry.fulfilled, (state, action) => {
			console.log("API Response:", action.payload);
			state.loading = false;
			state.validity = action.payload;
		});

		builder.addCase(fetchValidityEntry.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 3) ====== builders for getLeavePoliciesWithLimit ======

		builder.addCase(getLeavePoliciesWithLimit.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(getLeavePoliciesWithLimit.fulfilled, (state, action) => {
			console.log("API Response:", action.payload);
			state.loading = false;
			state.leavePolicyLimit = action.payload;
		});

		builder.addCase(getLeavePoliciesWithLimit.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 3) ====== builders for getDefaultValidity ======

		builder.addCase(getDefaultValidity.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(getDefaultValidity.fulfilled, (state, action) => {
			console.log("API Response:", action.payload);
			state.loading = false;
			state.defaultValidity = action.payload;
		});

		builder.addCase(getDefaultValidity.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});
		// 3) ====== builders for updateLeavePolicy ======

		builder.addCase(updateLeavePolicy.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(updateLeavePolicy.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(updateLeavePolicy.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 3) ====== builders for updateDefaultValidityId ======

		builder.addCase(updateDefaultValidityId.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(updateDefaultValidityId.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(updateDefaultValidityId.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// ====== builders for updateValidityApplyFor ======

		builder.addCase(updateValidityApplyFor.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(updateValidityApplyFor.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(updateValidityApplyFor.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});


		builder.addCase(updateValidityEntry.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(updateValidityEntry.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(updateValidityEntry.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// 4) ====== builders for deleteLeavePolicy  ======

		builder.addCase(deleteLeavePolicy.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(deleteLeavePolicy.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(deleteLeavePolicy.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload.message;
		});

		// ====== builders for getBulkUploadSampleData ======

		builder.addCase(getBulkUploadSampleData.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(getBulkUploadSampleData.fulfilled, (state, action) => {
			state.loading = false;
			state.bulkUploadSampleData = action.payload;
		});

		builder.addCase(getBulkUploadSampleData.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload?.message;
		});

		// ====== builders for bulkUpdateLeaveAllocation ======

		builder.addCase(bulkUpdateLeaveAllocation.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(bulkUpdateLeaveAllocation.fulfilled, (state, action) => {
			state.loading = false;
		});

		builder.addCase(bulkUpdateLeaveAllocation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload?.message;
		});
	},
});

export default leavePolicySlice.reducer;
export const { clearLeavePolicy } = leavePolicySlice.actions;
